import {
  type CountryCode,
  parsePhoneNumberFromString,
} from "libphonenumber-js";

/**
 * CHG-1049 (S6 of donor consolidation program) — vanilla-TS port of
 * the canonical donor helpers shipped in CHG-1037 (S2,
 * `web/dashboard/src/lib/donor.ts`). Same contract, same E.164 output
 * for the same input, so client and server agree byte-for-byte
 * across runtimes (doctrine ui-canonical-input-components.md Rule 16).
 *
 * The widget consumes this module from `checkout.ts` to assemble the
 * canonical wire-payload triplet (`donor_email` / `donor_phone` /
 * `donor_name`) on every auto-create endpoint hit:
 *
 *   - POST /payments (Stripe one-time intent) ✓ already correct pre-S6
 *   - POST /recurring-donations (Stripe subscription)
 *   - POST /payments/paypal/order
 *   - POST /payments/paypal/subscription   ← had `'anonymous@example.com'`
 *   - POST /donations (session record)
 *
 * Pre-S6 the PayPal subscription path used `donor_email:
 * this.donorInfo.email || 'anonymous@example.com'` as a literal
 * fallback — that fake address corrupted the donor record on the
 * server (phone-only donors got fake email rows linked, breaking
 * subsequent identity resolution). This module removes that hazard
 * structurally: `donorContactPayload` omits empty fields rather than
 * substituting fake values, and the caller's bi-channel pre-flight
 * (which already lives in `renderDonorInto`) blocks both-empty
 * submits at the widget layer.
 */

/* -- Types --------------------------------------------------------------- */

export interface DonorSelection {
  id?: string;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
}

export interface DonorContactPayload {
  donor_email?: string;
  donor_phone?: string;
  donor_phone_country?: string;
  donor_name?: string;
}

export interface DonorContactPayloadOptions {
  /** ISO-3166-1 alpha-2 hint passed to libphonenumber when the
   *  user-supplied phone has no country code. */
  countryHint?: CountryCode;
}

/* -- Helpers ------------------------------------------------------------- */

function nonEmpty(v: string | null | undefined): string | undefined {
  if (v === null || v === undefined) return undefined;
  const trimmed = String(v).trim();
  return trimmed.length === 0 ? undefined : trimmed;
}

/**
 * Map a {name, email, phone} input to the canonical wire payload. Empty fields
 * are OMITTED (no `null`, no fake-placeholder substitution). Phone is
 * normalized to E.164 and carries an additive ISO country hint when detected.
 * The donation checkout's canonical `phone-input.ts` preflight guarantees that
 * public form input is valid before this helper runs. The raw fallback remains
 * solely for trusted/legacy non-form consumers during migration; public API
 * schemas reject it before provider or donor creation.
 */
export function donorContactPayload(
  selection: DonorSelection | null | undefined,
  options: DonorContactPayloadOptions = {},
): DonorContactPayload {
  const out: DonorContactPayload = {};
  if (!selection) return out;

  const name = nonEmpty(selection.name);
  if (name) out.donor_name = name;

  const email = nonEmpty(selection.email);
  if (email) out.donor_email = email.toLowerCase();

  const phone = nonEmpty(selection.phone);
  if (phone) {
    let canonical: string | undefined;
    try {
      const parsed = phone.startsWith("+")
        ? parsePhoneNumberFromString(phone)
        : options.countryHint
          ? parsePhoneNumberFromString(phone, options.countryHint)
          : undefined;
      if (parsed && parsed.isValid()) {
        canonical = parsed.number;
        out.donor_phone_country = parsed.country ?? options.countryHint;
      }
    } catch {
      // libphonenumber may throw on malformed input; fall through.
    }
    out.donor_phone = canonical ?? phone;
  }

  return out;
}
